import type { Duration } from '@js-joda/core';
import type { QueryClient } from '@tanstack/query-core';
import type { Idea, IdeaPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type { Stage } from 'src/lib/tableStages';

export type IdeaDescriptor = Omit<Idea, 'key' | 'plan'> & { key?: Idea['key'] };

export interface IdeaConstructionOptions<TPlan extends IdeaPlanDTO = IdeaPlanDTO> {
    idea: IdeaDescriptor;
    plan: TPlan;
}

/**
 * Idea DTO
 *
 * This abstracts over the Idea type
 */
export class IdeaDTO<TPlan extends IdeaPlanDTO = IdeaPlanDTO> {
    constructor({ idea, plan }: IdeaConstructionOptions<TPlan>) {
        this.#idea = idea;
        this.#plan = plan;
        this.toStage = plan.toStage.bind(plan);
    }

    static fromIdeaAndPlan<TPlan extends IdeaPlanDTO>(ideaContract: IdeaDescriptor, planDto: TPlan): IdeaDTO<TPlan> {
        return new IdeaDTO({
            idea: ideaContract,
            plan: planDto,
        });
    }

    #idea: IdeaDescriptor;

    get idea(): IdeaDescriptor {
        return this.#idea;
    }

    get name(): string | null {
        return this.#idea.name;
    }

    #plan: TPlan;

    get plan() {
        return this.#plan;
    }

    readonly toStage: TPlan['toStage'];
}

export interface IdeaPlanToStageOptions {
    validationMode?: boolean;
    dwellTime?: Duration;
}

export abstract class IdeaPlanDTO {
    abstract toContract(): IdeaPlan;

    abstract toStage(
        inputStage: Stage | null,
        queryClient: QueryClient,
        options: IdeaPlanToStageOptions,
    ): Promise<Stage | null>;
}
